import React from 'react';
import { Code, Database, Cloud, Cog, Shield, ArrowRight } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <Code className="w-12 h-12 text-primary-600" />,
      title: "Software Development",
      description: "Custom software solutions tailored to your business needs",
      features: [
        "Custom Software Solutions",
        "Mobile and Web Application Development",
        "Enterprise Software Integration",
        "Software Architecture and Design",
        "Code review, and optimisation"
      ],
      gradient: "from-blue-500 to-blue-600"
    },
    {
      icon: <Database className="w-12 h-12 text-green-600" />,
      title: "Data Engineering",
      description: "Transform your data into actionable insights",
      features: [
        "Data Modelling & Warehousing",
        "Data Pipeline Management",
        "Data Integration",
        "Data Quality & Security",
        "Performance Optimisation"
      ],
      gradient: "from-green-500 to-green-600"
    },
    {
      icon: <Cloud className="w-12 h-12 text-purple-600" />,
      title: "Cloud Operations",
      description: "Scalable and secure cloud infrastructure solutions",
      features: [
        "Cloud Infrastructure Design and Implementation",
        "Cloud Migration and Optimisation",
        "Continuous Integration and Continuous Deployment (CI/CD) Pipelines"
      ],
      gradient: "from-purple-500 to-purple-600"
    },
    {
      icon: <Cog className="w-12 h-12 text-orange-600" />,
      title: "DevOps Services",
      description: "Streamline development and operations processes",
      features: [
        "Automation of Development Processes",
        "Infrastructure as Code (IaC)",
        "Monitoring and Performance Tuning"
      ],
      gradient: "from-orange-500 to-orange-600"
    },
    {
      icon: <Shield className="w-12 h-12 text-red-600" />,
      title: "Cloud Management",
      description: "Comprehensive cloud monitoring and support",
      features: [
        "24/7 Cloud Monitoring and Support",
        "Security Compliance and Risk Management",
        "Cloud Cost Optimization",
        "Cloud Operations policy and guideline"
      ],
      gradient: "from-red-500 to-red-600"
    }
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="section-padding bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
            Our Services
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            We offer a comprehensive suite of services encompassing Software development, 
            DevOps, Technology strategy, and cloud management.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-12">
          {services.map((service, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-secondary-100"
            >
              {/* Icon */}
              <div className="mb-6">
                <div className="p-4 bg-secondary-50 rounded-2xl w-fit">
                  {service.icon}
                </div>
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                {service.title}
              </h3>
              <p className="text-secondary-600 mb-6 leading-relaxed">
                {service.description}
              </p>

              {/* Features List */}
              <ul className="space-y-3 mb-8">
                {service.features.map((feature, featureIndex) => (
                  <li 
                    key={featureIndex}
                    className="flex items-start space-x-3 text-sm text-secondary-700"
                  >
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button 
                onClick={scrollToContact}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <span>Learn More</span>
                <ArrowRight size={16} />
              </button>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center bg-secondary-50 rounded-2xl p-8 md:p-12">
          <h3 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
            Ready to Scale Your Business?
          </h3>
          <p className="text-secondary-600 mb-8 max-w-2xl mx-auto">
            Let's discuss how our comprehensive suite of services can help transform 
            your digital infrastructure and accelerate your business growth.
          </p>
          <button 
            onClick={scrollToContact}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <span>Get Started Today</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
